import { useState, useEffect } from 'react';
import {
  Box, Container, Typography, Card, Grid, Button, TextField, Avatar,
  Dialog, DialogTitle, DialogContent, DialogActions, Alert, CircularProgress,
  Chip, IconButton, Divider, useMediaQuery, useTheme,
} from '@mui/material';
import {
  ArrowBack, Send, Cake, Wc, Flag, Church, Hotel, Event, School as SchoolIcon,
  Home, LocationCity, HistoryEdu, EditNote, AttachMoney, TrendingUp,
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';

import { locale } from '../utils/currency';
const API = process.env.REACT_APP_API_URL;
const hdrs = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const fmtDate = (d) => d
  ? new Date(d).toLocaleDateString(locale(), { day: '2-digit', month: 'short', year: 'numeric' })
  : '—';

const age = (dob) => {
  if (!dob) return null;
  const diff = Date.now() - new Date(dob).getTime();
  return Math.floor(diff / (365.25 * 24 * 3600 * 1000));
};

const Field = ({ icon: Icon, label, value }) => (
  <Grid item xs={6} sm={4}>
    <Box display="flex" gap={1} alignItems="flex-start">
      <Icon sx={{ fontSize: 18, color: 'text.disabled', mt: 0.3 }} />
      <Box minWidth={0}>
        <Typography variant="caption" color="text.secondary" display="block">{label}</Typography>
        <Typography variant="body2" fontWeight={600} sx={{ overflowWrap: 'break-word' }}>
          {value || '—'}
        </Typography>
      </Box>
    </Box>
  </Grid>
);

export default function ParentLearnerProfile() {
  const { admNo } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [learner, setLearner] = useState(null);
  const [teacher, setTeacher] = useState(null);
  const [correctDlg, setCorrectDlg] = useState(false);
  const [correction, setCorrection] = useState('');
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    const load = async () => {
      try {
        const [lRes, tRes] = await Promise.all([
          axios.get(`${API}/api/learners/${admNo}`, { headers: hdrs() }),
          axios.get(`${API}/api/academics/learner/${admNo}/class-teachers`, { headers: hdrs() }).catch(() => ({ data: [] })),
        ]);
        setLearner(lRes.data);
        setTeacher((tRes.data || [])[0] || null);
      } catch (e) {
        setError(e.response?.data?.error || 'Failed to load learner');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [admNo]);

  const sendCorrection = async () => {
    if (!correction.trim()) {
      setError('Please describe what needs to be corrected');
      return;
    }
    if (!teacher?.email) {
      setError('Class teacher does not have an email address on file. Please contact the school office directly.');
      return;
    }
    setSending(true); setError('');
    try {
      await axios.post(`${API}/api/messages`, {
        to: teacher.email,
        subject: `Correction request — ${learner.name} (${learner.admNo})`,
        body: correction,
        learnerAdmNo: learner.admNo,
      }, { headers: hdrs() });
      setSuccess(`Your correction request was sent to ${teacher.name}.`);
      setCorrectDlg(false);
      setCorrection('');
    } catch (e) {
      setError(e.response?.data?.error || 'Could not send the request');
    } finally { setSending(false); }
  };

  if (loading) return <Box textAlign="center" py={8}><CircularProgress /></Box>;

  if (!learner) return (
    <Container maxWidth="sm">
      <Box py={4}>
        <Alert severity="error">{error || 'Learner not found'}</Alert>
        <Button startIcon={<ArrowBack />} onClick={() => navigate('/parent')} sx={{ mt: 2 }}>
          Back to Dashboard
        </Button>
      </Box>
    </Container>
  );

  const yrs = age(learner.dob);

  return (
    <Container maxWidth="md">
      <Box py={3}>
        {/* Header */}
        <Box display="flex" alignItems="center" gap={2} mb={3}>
          <IconButton onClick={() => navigate('/parent')} sx={{ bgcolor: 'white', boxShadow: 1 }}>
            <ArrowBack />
          </IconButton>
          <Avatar sx={{ width: 56, height: 56, bgcolor: 'primary.main', fontSize: 24, fontWeight: 700 }}>
            {(learner.name || '?').charAt(0).toUpperCase()}
          </Avatar>
          <Box flex={1} minWidth={0}>
            <Typography variant="h5" fontWeight={800} noWrap>{learner.name}</Typography>
            <Typography variant="body2" color="text.secondary">
              Adm {learner.admNo} • {learner.stage || '—'}{learner.classCode ? ` • ${learner.classCode}` : ''}
            </Typography>
          </Box>
          <Chip label={learner.status || 'Active'} color={learner.status === 'Active' ? 'success' : 'default'} size="small" />
        </Box>

        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

        {/* Quick actions */}
        <Box display="flex" gap={1} mb={3} flexWrap="wrap">
          <Button size="small" variant="outlined" startIcon={<AttachMoney />} onClick={() => navigate('/parent/fees')}>
            Fees
          </Button>
          <Button size="small" variant="outlined" startIcon={<TrendingUp />} onClick={() => navigate('/parent/assessments')}>
            Results
          </Button>
          <Button size="small" variant="contained" startIcon={<EditNote />} onClick={() => setCorrectDlg(true)}>
            Request a Correction
          </Button>
        </Box>

        {/* Personal details */}
        <Card sx={{ p: { xs: 2, sm: 3 }, borderRadius: 3, mb: 2 }}>
          <Typography variant="subtitle1" fontWeight={800} mb={2}>Personal Details</Typography>
          <Grid container spacing={2.5}>
            <Field icon={Cake}   label="Date of Birth" value={learner.dob ? `${fmtDate(learner.dob)}${yrs != null ? ` (${yrs} yrs)` : ''}` : null} />
            <Field icon={Wc}     label="Gender"        value={learner.gender} />
            <Field icon={Flag}   label="Nationality"   value={learner.nationality} />
            <Field icon={Church} label="Denomination"  value={learner.denomination} />
            <Field icon={Hotel}  label="Student Type"  value={learner.studentType} />
          </Grid>
        </Card>

        {/* Schooling */}
        <Card sx={{ p: { xs: 2, sm: 3 }, borderRadius: 3, mb: 2 }}>
          <Typography variant="subtitle1" fontWeight={800} mb={2}>Schooling</Typography>
          <Grid container spacing={2.5}>
            <Field icon={SchoolIcon} label="Stage / Grade"   value={learner.stage} />
            <Field icon={SchoolIcon} label="Class"           value={learner.classCode} />
            <Field icon={Event}      label="Admission Date"  value={learner.admDate ? fmtDate(learner.admDate) : null} />
            <Field icon={HistoryEdu} label="Previous School" value={learner.previousSchool} />
          </Grid>
          {teacher && (
            <>
              <Divider sx={{ my: 2 }} />
              <Box display="flex" alignItems="center" gap={1.5}>
                <Avatar sx={{ width: 34, height: 34, bgcolor: '#00695c', fontSize: 15 }}>
                  {teacher.name.charAt(0)}
                </Avatar>
                <Box>
                  <Typography variant="body2" fontWeight={700}>{teacher.name}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Class Teacher{teacher.phone ? ` • ${teacher.phone}` : ''}
                  </Typography>
                </Box>
              </Box>
            </>
          )}
        </Card>

        {/* Home */}
        <Card sx={{ p: { xs: 2, sm: 3 }, borderRadius: 3, mb: 2 }}>
          <Typography variant="subtitle1" fontWeight={800} mb={2}>Home</Typography>
          <Grid container spacing={2.5}>
            <Field icon={Home}         label="Home Address"  value={learner.homeAddress} />
            <Field icon={LocationCity} label="Home District" value={learner.homeDistrict} />
          </Grid>
        </Card>

        <Alert severity="info" sx={{ borderRadius: 2 }}>
          These records are maintained by the school. Spotted something wrong? Use
          <strong> Request a Correction</strong> above and the class teacher will follow up.
        </Alert>

        {/* Correction request dialog */}
        <Dialog open={correctDlg} onClose={() => setCorrectDlg(false)} maxWidth="sm" fullWidth fullScreen={isMobile}>
          <DialogTitle>Request a Correction — {learner.name}</DialogTitle>
          <DialogContent dividers>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            {teacher ? (
              <>
                {!teacher.email && (
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    The class teacher doesn't have an email address on file. Please contact the school office directly.
                  </Alert>
                )}
                <Typography variant="body2" color="text.secondary" mb={2}>
                  Your request goes to <strong>{teacher.name}</strong> (class teacher). Describe
                  what is wrong and what it should be.
                </Typography>
                <TextField
                  fullWidth multiline rows={4} autoFocus
                  label="What needs correcting?"
                  placeholder="e.g. Date of birth should be 12 Mar 2015, not blank"
                  value={correction}
                  onChange={e => setCorrection(e.target.value)}
                />
              </>
            ) : (
              <Alert severity="warning">
                No class teacher is assigned to {learner.name}'s class yet, so the request
                can't be sent from here. Please contact the school office directly.
              </Alert>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setCorrectDlg(false)}>Cancel</Button>
            {teacher && (
              <Button variant="contained" startIcon={<Send />} onClick={sendCorrection}
                disabled={sending || !correction.trim() || !teacher.email}>
                {sending ? 'Sending…' : 'Send Request'}
              </Button>
            )}
          </DialogActions>
        </Dialog>
      </Box>
    </Container>
  );
}
